import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Database, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Hospital,
  Users,
  FileText,
  Wrench,
  ArrowLeft,
  Play,
  Eye,
  Zap,
  Stethoscope
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdminUtilities() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hospitalSummary, setHospitalSummary] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [fixResult, setFixResult] = useState(null);
  const [hospitalName, setHospitalName] = useState('General Hospital');
  const [error, setError] = useState(null);

  // Check if user is admin
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [navigate]);

  const runDiagnosis = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try full-system-check first, fallback to diagnose-my-account
      let response;
      try {
        response = await api.get('/admin/full-system-check');
      } catch (e) {
        console.log('full-system-check failed, trying diagnose-my-account');
        response = await api.get('/admin/diagnose-my-account');
      }
      setDiagnosis(response.data);
      if (response.data.status === 'ISSUES_FOUND') {
        toast.warning('Issues found! See details below.');
      } else if (response.data.status === 'ERROR') {
        toast.error('Error running check');
      } else {
        toast.success('No issues found');
      }
    } catch (err) {
      console.error('Diagnosis error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to run system check');
      toast.error('Failed to run system check');
    }
    setLoading(false);
  };

  const forceFixAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/admin/force-fix-my-account');
      setFixResult(response.data);
      if (response.data.success) {
        toast.success('Account fixed! Please logout and login again.');
      } else {
        toast.error('Failed to fix account');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fix account');
      toast.error('Failed to fix account');
    }
    setLoading(false);
  };

  const fetchHospitalSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/hospital-summary');
      setHospitalSummary(response.data);
      toast.success('Hospital summary loaded');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch hospital summary');
      toast.error('Failed to fetch hospital summary');
    }
    setLoading(false);
  };

  const fixAllData = async (dryRun = true) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(`/admin/fix-all-hospital-data?hospital_name=${encodeURIComponent(hospitalName)}&dry_run=${dryRun}`);
      setFixResult(response.data);
      if (dryRun) {
        toast.success('Preview complete - review changes below');
      } else {
        toast.success('Fixes applied! Please LOGOUT and LOGIN again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fix data');
      toast.error('Failed to fix data');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Utilities</h1>
            <p className="text-slate-600">Fix data visibility issues with one click</p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div>{error}</div>
              <div className="text-xs mt-2 text-slate-600">
                If this persists, try refreshing the page or logging out and back in.
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* STEP 1: Diagnose */}
        <Card className="border-2 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2 text-red-800">
              <Stethoscope className="h-6 w-6" />
              Step 1: Full System Check
            </CardTitle>
            <CardDescription className="text-red-700">
              Click this first to see everything about your account and data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={runDiagnosis} 
              disabled={loading}
              size="lg"
              className="w-full bg-red-600 hover:bg-red-700 text-lg py-6"
            >
              {loading ? <RefreshCw className="h-5 w-5 animate-spin mr-2" /> : <Stethoscope className="h-5 w-5 mr-2" />}
              Run Full System Check
            </Button>
          </CardContent>
        </Card>

        {/* Diagnosis Results */}
        {diagnosis && (
          <Card className={diagnosis.status === 'ISSUES_FOUND' ? 'border-2 border-orange-500' : diagnosis.status === 'ERROR' ? 'border-2 border-red-500' : 'border-2 border-green-500'}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {diagnosis.status === 'ISSUES_FOUND' ? (
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                ) : diagnosis.status === 'ERROR' ? (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}
                System Check Results
                <Badge variant={diagnosis.status === 'ISSUES_FOUND' ? 'destructive' : diagnosis.status === 'ERROR' ? 'destructive' : 'default'}>
                  {diagnosis.status}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* User Info */}
              <div className="bg-slate-100 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Your Account</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Email:</strong> {diagnosis.current_user?.email}</div>
                  <div><strong>Role:</strong> {diagnosis.current_user?.role}</div>
                  <div className="col-span-2"><strong>Hospital ID:</strong> <code className="text-xs bg-white px-1">{diagnosis.current_user?.hospital_id}</code></div>
                </div>
              </div>

              {/* Hospital Status */}
              <div className={`p-4 rounded-lg ${diagnosis.user_hospital?.exists ? 'bg-green-100' : 'bg-red-100'}`}>
                <h4 className="font-medium mb-2">Your Hospital</h4>
                <div><strong>Name:</strong> {diagnosis.user_hospital?.name}</div>
                <div><strong>Exists:</strong> {diagnosis.user_hospital?.exists ? '✅ Yes' : '❌ No'}</div>
              </div>
              
              {/* Patient Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-100 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold">{diagnosis.patients?.for_your_hospital || 0}</div>
                  <div className="text-xs text-slate-600">Your Patients</div>
                </div>
                <div className="bg-slate-100 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold">{diagnosis.patients?.total_in_db || 0}</div>
                  <div className="text-xs text-slate-600">Total Patients</div>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg text-center">
                  <div className="text-xl font-bold">{diagnosis.hospitals?.total || 0}</div>
                  <div className="text-xs text-slate-600">Total Hospitals</div>
                </div>
              </div>

              {/* Problems */}
              {diagnosis.problems?.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Problems Found:</strong>
                    <ul className="list-disc ml-4 mt-2">
                      {diagnosis.problems.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Recommended Fixes */}
              {diagnosis.recommended_fixes?.length > 0 && (
                <Alert className="border-blue-500 bg-blue-50">
                  <AlertDescription>
                    <strong>Recommended Fixes:</strong>
                    <ul className="list-disc ml-4 mt-2">
                      {diagnosis.recommended_fixes.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Quick Fix Button */}
              {diagnosis.recommended_hospital_id && (
                <div className="bg-green-100 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Quick Fix Available!</h4>
                  <p className="text-sm mb-3">
                    We found that most patients are in hospital "{diagnosis.recommended_hospital_name}". 
                    Click below to fix your account.
                  </p>
                  <Button 
                    onClick={forceFixAccount}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700"
                    size="lg"
                  >
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                    Fix My Account Now (Use Hospital: {diagnosis.recommended_hospital_name})
                  </Button>
                </div>
              )}

              {/* Duplicate Hospitals */}
              {diagnosis.duplicate_hospitals?.length > 1 && (
                <div className="mt-4">
                  <strong>Hospitals with similar names:</strong>
                  <table className="w-full text-sm mt-2">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-2">Hospital Name</th>
                        <th className="text-center p-2">Patients</th>
                        <th className="text-left p-2">Hospital ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnosis.duplicate_hospitals.map((h, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-2">{h.name}</td>
                          <td className="text-center p-2">
                            <Badge>{h.patient_count || 0}</Badge>
                          </td>
                          <td className="p-2 text-xs font-mono">{h.id?.slice(0, 12)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* All Hospitals List */}
              {diagnosis.hospitals?.list && (
                <details className="mt-4">
                  <summary className="cursor-pointer font-medium">View All Hospitals ({diagnosis.hospitals.total})</summary>
                  <div className="mt-2 max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagnosis.hospitals.list.map((h, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2">{h.name}</td>
                            <td className="p-2 text-xs font-mono">{h.id?.slice(0, 12)}...</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        )}

        {/* STEP 2: Fix All Data */}
        <Card className="border-2 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2 text-green-800">
              <Zap className="h-6 w-6" />
              Step 2: Fix All Data (One Click)
            </CardTitle>
            <CardDescription className="text-green-700">
              This will merge all data for your hospital and fix your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hospitalName">Hospital Name to Fix</Label>
              <Input
                id="hospitalName"
                value={hospitalName}
                onChange={(e) => setHospitalName(e.target.value)}
                placeholder="Enter hospital name (e.g., General Hospital)"
                className="bg-white"
              />
              <p className="text-xs text-slate-600">
                Enter your hospital name exactly as shown in the diagnosis above
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Button 
                onClick={() => fixAllData(true)} 
                disabled={loading || !hospitalName}
                size="lg"
                variant="outline"
                className="border-green-600 text-green-700 hover:bg-green-100"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview Changes (Safe)
              </Button>
              <Button 
                onClick={() => fixAllData(false)} 
                disabled={loading || !hospitalName}
                size="lg"
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                Apply Fixes Now
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fix Results */}
        {fixResult && (
          <Card className={fixResult.mode === 'LIVE' ? 'border-2 border-green-500' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Fix Results
                <Badge variant={fixResult.mode === 'LIVE' ? 'default' : 'secondary'}>
                  {fixResult.mode}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className={fixResult.mode === 'LIVE' ? 'border-green-500 bg-green-50' : ''}>
                <AlertDescription>
                  <strong>{fixResult.message}</strong>
                  {fixResult.action_required && (
                    <div className="mt-2 text-lg font-bold text-green-700">
                      ⚠️ {fixResult.action_required}
                    </div>
                  )}
                </AlertDescription>
              </Alert>

              {fixResult.primary_hospital && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <strong>Primary Hospital (all data will be merged here):</strong>
                  <div className="mt-2">
                    <div>Name: {fixResult.primary_hospital.hospital_name}</div>
                    <div>Patients: {fixResult.primary_hospital.patients}</div>
                    <div>Users: {fixResult.primary_hospital.users}</div>
                    <div>Referrals: {fixResult.primary_hospital.referrals}</div>
                  </div>
                </div>
              )}

              {fixResult.fixes_applied?.length > 0 && (
                <div>
                  <strong>Changes {fixResult.mode === 'LIVE' ? 'Applied' : 'To Be Applied'}:</strong>
                  <ul className="list-disc ml-6 mt-2 text-sm">
                    {fixResult.fixes_applied.map((fix, i) => (
                      <li key={i} className="text-green-700">{fix}</li>
                    ))}
                  </ul>
                </div>
              )}

              {fixResult.mode === 'LIVE' && (
                <div className="mt-4 p-4 bg-yellow-100 rounded-lg text-center">
                  <strong className="text-lg">✅ Fixes Applied!</strong>
                  <p className="mt-2">Please click the button below to logout, then login again to see your data.</p>
                  <Button 
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = '/login';
                    }}
                    className="mt-4 bg-yellow-600 hover:bg-yellow-700"
                    size="lg"
                  >
                    Logout Now
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Hospital Summary (optional) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hospital className="h-5 w-5" />
              View All Hospitals (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={fetchHospitalSummary} 
              disabled={loading}
              variant="outline"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              View Hospital Summary
            </Button>
            
            {hospitalSummary && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-100">
                      <th className="text-left p-2">Hospital Name</th>
                      <th className="text-center p-2">Users</th>
                      <th className="text-center p-2">Patients</th>
                      <th className="text-center p-2">Referrals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hospitalSummary.hospitals?.slice(0, 10).map((h, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-medium">{h.hospital_name}</td>
                        <td className="text-center p-2">{h.users}</td>
                        <td className="text-center p-2">
                          <Badge variant={h.patients > 0 ? "default" : "secondary"}>
                            {h.patients}
                          </Badge>
                        </td>
                        <td className="text-center p-2">{h.referrals_v2}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-800">📋 Simple Instructions</CardTitle>
          </CardHeader>
          <CardContent className="text-amber-900">
            <ol className="list-decimal ml-4 space-y-3 text-lg">
              <li><strong>Click "Diagnose My Account"</strong> - This shows what's wrong</li>
              <li><strong>Check the hospital name</strong> - Make sure it matches your hospital</li>
              <li><strong>Click "Preview Changes"</strong> - See what will be fixed (safe)</li>
              <li><strong>Click "Apply Fixes Now"</strong> - Fix everything!</li>
              <li><strong>Click "Logout Now"</strong> - Then login again</li>
              <li><strong>Check Patients page</strong> - Your data should now be visible!</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
